"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { ShieldCheck, Banknote, Megaphone, Wrench, ChartLine, ClipboardCheck, Lightbulb, ArrowRight, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const servicesData = [{
  id: "tenant-screening",
  title: "Tenant Screening",
  description: "We conduct comprehensive background checks, including credit history, criminal records, employment verification, and past eviction reports. Our goal is to place reliable, long-term tenants in your Kansas City properties.",
  proTip: "A rigorous screening process is your first line of defense. It significantly reduces turnover rates, minimizes late payments, and protects your physical asset from unnecessary wear and tear.",
  icon: ShieldCheck
}, {
  id: "rent-collection",
  title: "Rent Collection",
  description: "Our streamlined online payment portals make it easy for tenants to pay on time. We handle all collection efforts, enforce lease terms strictly, and ensure direct, timely deposits to your owner account.",
  proTip: "Consistent, professional rent collection policies are key to maintaining steady cash flow and avoiding the awkwardness of personal collection efforts.",
  icon: Banknote
}, {
  id: "property-marketing",
  title: "Property Marketing",
  description: "We utilize high-quality photography, compelling descriptions, and strategic listing placements across top rental platforms (Zillow, Trulia, etc.) to minimize vacancy times and attract premium renters.",
  proTip: "Professional photos and targeted local marketing can rent a property up to 30% faster in the competitive Kansas City metro market.",
  icon: Megaphone
}, {
  id: "maintenance-coordination",
  title: "Maintenance Coordination",
  description: "We provide 24/7 response to maintenance requests using our established network of trusted, vetted local contractors. We handle everything from minor repairs to emergency situations, keeping you informed every step of the way.",
  proTip: "Prompt, professional maintenance not only keeps tenants happy (encouraging lease renewals) but prevents minor issues from becoming costly long-term damage.",
  icon: Wrench
}, {
  id: "financial-reporting",
  title: "Financial Reporting",
  description: "Access transparent, detailed monthly statements and annual tax documents anytime via your secure owner portal. Track income, expenses, and overall portfolio performance with ease.",
  proTip: "Keep your personal and rental finances strictly separate. Comprehensive reporting makes tax preparation seamless and helps you identify areas to maximize ROI.",
  icon: ChartLine
}, {
  id: "property-inspections",
  title: "Property Inspections",
  description: "We conduct thorough move-in, move-out, and routine periodic inspections to ensure lease compliance and identify preventative maintenance needs before they escalate.",
  proTip: "Routine inspections demonstrate to tenants that you care about the property, encouraging them to treat it with the same level of respect.",
  icon: ClipboardCheck
}];
function ServiceBlock({
  service,
  index
}: {
  service: (typeof servicesData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const Icon = service.icon;
  return <div ref={ref} id={service.id} data-index={index} className={`scroll-mt-32 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
        <div className="flex-shrink-0 p-4 bg-primary/5 rounded-2xl border border-border/50 shadow-sm">
          <Icon className="w-10 h-10 text-primary" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 tracking-tight">
            {service.title}
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            {service.description}
          </p>

          {}
          <div className="relative overflow-hidden bg-secondary/10 border-l-4 border-secondary rounded-r-xl p-6 md:p-8 shadow-sm">
            <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
              <Building className="w-24 h-24 text-secondary" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-1.5 bg-secondary/20 rounded-md">
                  <Lightbulb className="w-5 h-5 text-secondary-foreground" />
                </div>
                <h4 className="font-semibold text-foreground tracking-wide uppercase text-sm">
                  Investor Pro Tip
                </h4>
              </div>
              <p className="text-foreground/80 font-medium leading-relaxed">
                {service.proTip}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {index !== servicesData.length - 1 && <div className="w-full h-px bg-border/60 my-16 md:my-24" />}
    </div>;
}
export default function FullServiceManagement() {
  const [activeSection, setActiveSection] = useState<string>(servicesData[0].id);
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = servicesData.map(s => document.getElementById(s.id));
      let currentActive = activeSection;
      for (const el of sectionElements) {
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 200) {
            currentActive = el.id;
          }
        }
      }
      if (currentActive !== activeSection) {
        setActiveSection(currentActive);
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);
  return <section id="full-service-management" className="bg-background py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,var(--color-muted)_0%,transparent_40%)] opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-16 md:mb-24 max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight mb-6">
            Comprehensive Management Solutions
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            We handle the day-to-day complexities of property management so you can focus on growing your portfolio. Discover our full suite of services designed for Kansas City investors.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {}
          <div className="hidden lg:block lg:col-span-4">
            <div className="sticky top-32 p-6 bg-card rounded-2xl border border-border shadow-sm">
              <h3 className="text-lg font-semibold text-foreground mb-6 uppercase tracking-wider">
                Table of Contents
              </h3>
              <nav className="flex flex-col space-y-2">
                {servicesData.map((service, i) => <Link key={service.id} to={`#${service.id}`} data-index={i} className={`group flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${activeSection === service.id ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"}`}>
                    <span className="font-medium">{service.title}</span>
                    <ArrowRight className={`w-4 h-4 transition-transform duration-200 ${activeSection === service.id ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"}`} />
                  </Link>)}
              </nav>

              <div className="mt-10 pt-8 border-t border-border">
                <p className="text-sm text-muted-foreground mb-4">
                  Ready to optimize your rental portfolio?
                </p>
                <Button className="w-full" size="lg" asChild>
                  <Link to="#">Request Consultation</Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            {servicesData.map((service, index) => <ServiceBlock key={service.id} service={service} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}